import { useState } from "react";
import { BellRing, CalendarClock, CheckCircle2, Lock, Loader2, Mail, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { captureLead } from "@/api/client";

const BENEFITS = [
  {
    icon: BellRing,
    title: "Lembrete de renovação",
    description: "Avisamos 60 dias antes do seu certificado vencer — sem cobrança.",
  },
  {
    icon: CalendarClock,
    title: "Calendário fiscal",
    description: "Prazos de eSocial, EFD-Reinf, NF-e e DCTFWeb direto no seu e-mail.",
  },
  {
    icon: Lock,
    title: "Conteúdo 100% sem spam",
    description: "1 e-mail por semana, no máximo. Cancele com 1 clique.",
  },
];

export const NewsletterCTA = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await captureLead({
        email: email.trim(),
        source: "newsletter-home",
      });
      setDone(true);
      toast.success("Inscrição confirmada! 🎉");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao inscrever");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-16 md:py-20 bg-gradient-brand text-white">
      <div className="container max-w-4xl">
        <div className="text-center space-y-5">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm px-4 py-1.5">
            <Mail className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">
              Receba dicas semanais
            </span>
          </div>

          <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight max-w-2xl mx-auto">
            Nunca mais esqueça de renovar seu certificado
          </h2>

          <p className="text-white/80 max-w-xl mx-auto">
            Mais de <strong className="text-white">3.500 profissionais e empresas</strong> já recebem nossos alertas de vencimento e prazos fiscais. Grátis, semanal, sem spam.
          </p>

          {done ? (
            <div className="inline-flex items-center gap-3 rounded-2xl bg-white/15 backdrop-blur-sm px-6 py-4 text-left">
              <CheckCircle2 className="h-8 w-8 text-success-foreground bg-success rounded-full p-1.5 shrink-0" />
              <div>
                <p className="font-bold text-base">Pronto, você está dentro!</p>
                <p className="text-xs text-white/80">Confira sua caixa de entrada nos próximos minutos.</p>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <Input
                type="email"
                required
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white text-foreground placeholder:text-muted-foreground border-0 h-12"
              />
              <Button
                type="submit"
                disabled={submitting}
                size="lg"
                variant="secondary"
                className="bg-white text-primary hover:bg-white/90 font-bold shadow-md"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : null}
                Quero receber
              </Button>
            </form>
          )}

          {/* Social proof — avatars placeholder + inscribers count */}
          <div className="inline-flex items-center gap-3 rounded-full bg-white/10 backdrop-blur-sm px-4 py-2">
            <div className="flex -space-x-2">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-6 w-6 rounded-full bg-white/30 border-2 border-white/50 flex items-center justify-center text-[9px] font-bold text-white"
                >
                  {String.fromCharCode(65 + i)}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1 text-xs text-white/90">
              <Users className="h-3 w-3" />
              <span><strong>3.542</strong> inscritos esta semana</span>
            </div>
          </div>
        </div>

        {/* Benefits grid */}
        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {BENEFITS.map((b) => {
            const Icon = b.icon;
            return (
              <div
                key={b.title}
                className="flex items-start gap-3 rounded-2xl bg-white/10 backdrop-blur-sm p-4 border border-white/15"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/20">
                  <Icon className="h-4 w-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-white leading-tight">{b.title}</p>
                  <p className="text-xs text-white/75 mt-1">{b.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-6 text-center text-[11px] text-white/60">
          Ao se inscrever você concorda em receber comunicações da Biz Certificadora Digital conforme nossa{" "}
          <a href="/privacidade" className="underline hover:text-white">
            política de privacidade
          </a>{" "}
          (LGPD).
        </p>
      </div>
    </section>
  );
};
